import { useLocale, useTranslator } from '@ianesthesie-kit/core';
import { useCallback } from 'react';
import {
  isTranslatable,
  isUserInput,
  MedicalHoItem,
  MedicalHoItemType,
  needsQuestionTranslation
} from '@/modules/surgicalEvent';
import { useDateFormatter } from './useDateFormatter';
import { SnomedService } from '@/modules/snomed/snomed.service';

export const useMedicalHoItemTranslator = () => {
  const translate = useTranslator();
  const formatDate = useDateFormatter();
  const locale = useLocale();

  return useCallback(
    (arg: MedicalHoItem) => {
      const doTranslate = (
        item: MedicalHoItem,
        translateQcodeIfApplicable = true, // set this to false to skip translationQCode prefix
        joinQcodeWith = ': '
      ): string => {
        if (item.mergedWith?.length) {
          const parentTranslation = doTranslate({
            ...item,
            mergedWith: undefined
          });

          // PrematureBirth fuses the gestational term onto the parent badge
          // ("Prématuré 33 SA") and keeps the birth weight as its own segment.
          // The .short labels position the value (and its unit) themselves via a
          // {value} placeholder, e.g. "{value} SA" / "Poids à la naissance {value}kg".
          if (item.qcode === 'PrematureBirth') {
            const renderValue = (it: MedicalHoItem) => {
              const num = +it.code;
              const value = Number.isNaN(num) ? it.code : `${num}`;
              return it.translationQcode
                ? translate(it.translationQcode, undefined, { value })
                : value;
            };
            const term = item.mergedWith.find(
              (it) => it.qcode === 'PrematureBirthTerm'
            );
            const rest = item.mergedWith.filter((it) => it !== term);
            const head = term
              ? `${parentTranslation} ${renderValue(term)}`
              : parentTranslation;
            return [head].concat(rest.map(renderValue)).join(' - ');
          }

          const childTranslations = item.mergedWith.map((it) =>
            doTranslate(
              it,
              !isUserInput(it.sourceType),
              it.type === MedicalHoItemType.NUMBER ? '' : undefined
            )
          );
          if (
            item.mergedWith.length === 1 &&
            item.mergedWith[0].type === MedicalHoItemType.DATE
          ) {
            return `${parentTranslation} (${childTranslations[0]})`;
          }

          return [parentTranslation].concat(childTranslations).join(' - ');
        }
        if (item.snomedCode) {
          return (
            SnomedService.getInstance().getCodeTranslation(
              item.snomedCode,
              locale ?? ''
            ) ?? item.code
          );
        }
        let prepend = '';
        if (translateQcodeIfApplicable && needsQuestionTranslation(item)) {
          prepend = translate(item.translationQcode);
          prepend += joinQcodeWith;
        }

        const codeTranslation = () => {
          if (isTranslatable(item)) {
            return translate(item.translationCode);
          }

          if (item.type === MedicalHoItemType.DATE) {
            const pattern = item.isApproximateDate
              ? item.isApproximateDate === 'yearOnly'
                ? 'yyyy'
                : 'MMMM yyyy'
              : undefined;
            return formatDate(item.code, pattern);
          }

          if (item.type === MedicalHoItemType.NUMBER) {
            const num = +item.code;
            // convert the string to a number so numbers like 1.0 will be displayed like integers
            if (!Number.isNaN(num)) {
              return num;
            }
          }

          if (item.isScore && item.type === MedicalHoItemType.TEXT) {
            if (/^medical_synthesis\..*[^\w._].*$/.test(item.code)) {
              const [translationId, val] = item.code.split(/\s+/, 2);
              return `${translate(translationId)} ${val}`;
            }
          }

          return item.code;
        };

        return `${prepend}${codeTranslation()}`;
      };

      return doTranslate(arg);
    },
    [formatDate, translate, locale]
  );
};
